/**
 * Timeline: scenes strip, an overview row (camera, markers, captions, comments; expandable), the
 * layers (outline · fill · hold · exit segments) and audio tracks with waveforms. The ruler, scenes
 * and overview stay pinned at the top and the audio at the bottom; the layers scroll in between.
 *
 * Layers show the scene under the playhead (or every scene), one row each or packed into shared
 * rows. Drag bars to move, drag segment edges to retime; everything snaps to frames, the playhead
 * and other edges (hold Alt to disable snapping). With Ripple on (or Shift held while dragging), moving
 * a scene's start or an audio clip moves everything after it too, so later timing is kept. Zoom with the controls in the layers header,
 * Ctrl+wheel or a trackpad pinch (around the pointer), = and - (around the playhead) and \ (fit).
 */

import { clipDuration, elementTimes, frameRect, INF, projectDuration, sortedScenes, type AudioClip, type Element, type ElementTimes, type Project } from "@studio/engine";
import { Camera, Captions, ChevronDown, ChevronRight, Flag, Maximize2, ZoomIn, ZoomOut, Image as ImageIcon, Layers, MessageSquare, Mic, Music, Plus, Shapes, Type, Wand2 } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { addScene, rippleMin, rippleShift } from "./actions";
import { fileUrl } from "../api";
import type { Preview } from "./preview";
import { selectedElements, selectElement, useEditor, type TimelineView } from "./store";

const LABEL_W = 170;
const AUDIO_ROW_H = 41;
const SNAP_PX = 6;
const MIN_PPS = 2;
const MAX_PPS = 1500; // ~50 px per frame at 30 fps
const clampPps = (v: number) => Math.min(MAX_PPS, Math.max(MIN_PPS, v));

export function Timeline({ preview }: { preview: Preview }) {
  const project = useEditor((s) => s.project)!;
  const scroller = useRef<HTMLDivElement>(null);
  const [pps, setPps] = useState(0); // pixels per second; 0 = not yet fitted
  const dur = projectDuration(project);
  const total = Math.max(dur + 5, 20);

  useEffect(() => {
    if (pps === 0 && scroller.current) setPps(clampPps((scroller.current.clientWidth - LABEL_W - 40) / Math.max(dur, 10)));
  }, [pps, dur]);

  const ppsRef = useRef(pps);
  ppsRef.current = pps;
  const pendingScroll = useRef<number | null>(null); // scrollLeft set for the next frame
  const scrollTo = (x: number) => {
    pendingScroll.current = x;
    requestAnimationFrame(() => {
      if (scroller.current && pendingScroll.current !== null) scroller.current.scrollLeft = pendingScroll.current;
      pendingScroll.current = null;
    });
  };
  const laneW = () => Math.max(100, (scroller.current?.clientWidth ?? 800) - LABEL_W);
  /** Zoom keeping time `t` at `px` pixels into the visible lanes (default: the playhead if it's in
   *  view, else the middle of the view). */
  const zoomTo = (next: number, t?: number, px?: number) => {
    const el = scroller.current;
    if (!el) return;
    const cur = ppsRef.current;
    const left = pendingScroll.current ?? el.scrollLeft;
    next = clampPps(next);
    if (t === undefined) {
      const now = useEditor.getState().time;
      const x = now * cur - left;
      t = x >= 0 && x <= laneW() ? now : (left + laneW() / 2) / cur;
    }
    const at = px ?? t * cur - left;
    ppsRef.current = next; // several key presses can arrive before the next render
    setPps(next);
    scrollTo(t * next - at);
  };
  const fit = (t0: number, t1: number) => {
    const el = scroller.current;
    if (!el) return;
    const next = clampPps((laneW() - 40) / Math.max(t1 - t0, 0.5));
    ppsRef.current = next;
    setPps(next);
    scrollTo(Math.max(0, t0 * next - 20));
  };
  const fitAll = () => fit(0, Math.max(dur, 1));

  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return; // trackpad pinches arrive as ctrl+wheel too
    e.preventDefault();
    const el = scroller.current!;
    const px = e.clientX - el.getBoundingClientRect().left - LABEL_W;
    const factor = Math.exp(-Math.max(-60, Math.min(60, e.deltaY)) * 0.006);
    const cur = ppsRef.current;
    zoomTo(cur * factor, ((pendingScroll.current ?? el.scrollLeft) + px) / cur, px);
  };

  useEffect(() => {
    const onZoom = (e: Event) => {
      const d = (e as CustomEvent<"in" | "out" | "fit">).detail;
      if (d === "fit") fitAll();
      else zoomTo(ppsRef.current * (d === "in" ? 1.5 : 1 / 1.5));
    };
    window.addEventListener("studio:zoom", onZoom);
    return () => window.removeEventListener("studio:zoom", onZoom);
  });

  const view = useEditor((s) => s.timeline);
  const sceneId = useEditor((s) => (s.project ? sceneAt(s.project, s.time)?.id ?? null : null));
  const times = useMemo(() => elementTimes(project), [project]);
  const scene = project.scenes.find((x) => x.id === sceneId) ?? null;

  const elements = useMemo(() => {
    let list = [...project.elements];
    if (view.scope === "scene" && scene) {
      const [t0, t1] = sceneSpan(project, scene.id);
      // this scene's layers, plus any still on the board from earlier "build" scenes (not ones
      // that are only being wiped away by this scene's transition, and not drawings left behind
      // elsewhere on the board when the camera panned away: their scene's frame isn't this one)
      const kept = (tm: ElementTimes) => tm.enter0 < t1 && Math.min(tm.exit1, tm.clear0) > t0 + 1e-6;
      const here = frameRect(project, scene);
      const frames = new Map(project.scenes.map((s) => [s.id, frameRect(project, s)]));
      const inView = (sceneId: string) => {
        const f = frames.get(sceneId);
        return !!f && f.x < here.x + here.w && here.x < f.x + f.w && f.y < here.y + here.h && here.y < f.y + f.h;
      };
      list = list.filter((el) => el.sceneId === scene.id || (kept(times.get(el.id)!) && inView(el.sceneId)));
    }
    return list.sort((a, b) => times.get(a.id)!.enter0 - times.get(b.id)!.enter0 || a.z - b.z);
  }, [project, times, view.scope, scene]);
  const lanes = useMemo(() => (view.pack ? packLanes(elements, times) : null), [view.pack, elements, times]);
  const tracks = useMemo(() => audioTracks(project), [project]);
  useJumpToSelection(elements, times);
  // audio stays pinned at the bottom only while it takes less than half the timeline's height
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHeight(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pinAudio = tracks.length * AUDIO_ROW_H <= height * 0.5;

  if (pps === 0) return <div className="timeline" ref={scroller} />;
  const width = total * pps;

  return (
    <div className={`timeline ${view.compact ? "compact" : ""}`} ref={scroller} onWheel={onWheel}>
      <div className="tl-inner" style={{ width: width + LABEL_W }}>
        <div className="tl-head">
          <Ruler pps={pps} width={width} total={total} preview={preview} />
          <ScenesRow project={project} pps={pps} total={total} />
          {view.expanded ? (
            <>
              <CameraRow project={project} pps={pps} preview={preview} toggle />
              <MarkersRow project={project} pps={pps} />
              {project.subtitles.length > 0 && <CaptionsRow project={project} pps={pps} />}
              <CommentsRow pps={pps} />
            </>
          ) : (
            <OverviewRow project={project} pps={pps} preview={preview} />
          )}
          <LayersHeader view={view} count={elements.length} scene={view.scope === "scene" ? scene : null}
            zoom={{ pps, span: laneW() / pps, set: (v) => zoomTo(v), fitAll, fitScene: scene ? () => fit(...sceneSpan(project, scene.id)) : null }} />
        </div>
        {lanes
          ? lanes.map((lane, i) => <LaneRow key={"l" + i} index={i} lane={lane} project={project} pps={pps} total={total} />)
          : elements.map((el) => <ElementRow key={el.id} el={el} project={project} pps={pps} total={total} other={view.scope === "scene" && el.sceneId !== sceneId} />)}
        {elements.length === 0 && (
          <div className="tl-row tl-empty"><div className="tl-label" /><div className="tl-lane muted small">{view.scope === "scene" ? "No layers in this scene yet." : "No layers yet."}</div></div>
        )}
        <div className="tl-row tl-filler" onPointerDown={(e) => scrubFrom(e, pps, preview)}>
          <div className="tl-label" />
        </div>
        <div className={`tl-foot ${pinAudio ? "" : "unpinned"}`} title={pinAudio ? undefined : "Too many audio tracks to keep pinned; Tidy tracks (wand) can merge them"}>
          {tracks.map((tr, i) => (
            <AudioRow key={"t" + tr.track} track={tr.track} label={tr.label} project={project} pps={pps} first={i === 0} />
          ))}
        </div>
        <Playhead pps={pps} scroller={scroller} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------- layout

/** The scene the time falls in (the last one starting at or before it). */
export function sceneAt(project: Project, t: number) {
  const scenes = sortedScenes(project);
  let cur = scenes[0] ?? null;
  for (const s of scenes) if (s.start <= t + 1e-6) cur = s;
  return cur;
}

function sceneSpan(project: Project, id: string): [number, number] {
  const scenes = sortedScenes(project);
  const i = scenes.findIndex((s) => s.id === id);
  return [scenes[i].start, i + 1 < scenes.length ? scenes[i + 1].start : INF];
}

/** Greedy interval packing: each layer goes in the first row that is free for its whole life. */
function packLanes(elements: Element[], times: Map<string, ElementTimes>): Element[][] {
  const lanes: { end: number; items: Element[] }[] = [];
  for (const el of elements) {
    const tm = times.get(el.id)!;
    const end = Number.isFinite(tm.end) ? tm.end : INF;
    const lane = lanes.find((l) => l.end <= tm.enter0 + 1e-6);
    if (lane) {
      lane.items.push(el);
      lane.end = end;
    } else lanes.push({ end, items: [el] });
  }
  return lanes.map((l) => l.items);
}

/** Voice tracks first, then music, numbered from 1 within each group whatever their stored numbers. */
function audioTracks(project: Project): { track: number; label: string }[] {
  const nums = [...new Set(project.audio.map((c) => c.track))].sort((a, b) => a - b);
  if (nums.length === 0) return [{ track: 0, label: "Voice 1" }];
  const isMusic = (t: number) => {
    const cs = project.audio.filter((c) => c.track === t);
    return cs.filter((c) => c.role === "music").length > cs.length / 2;
  };
  const voice = nums.filter((t) => !isMusic(t));
  const music = nums.filter(isMusic);
  return [...voice.map((t, i) => ({ track: t, label: `Voice ${i + 1}` })), ...music.map((t, i) => ({ track: t, label: `Music ${i + 1}` }))];
}

/** Voice clips share the fewest tracks without overlapping; music goes on tracks below them. One undo step. */
export function tidyTracks() {
  const { project, edit } = useEditor.getState();
  if (!project) return;
  const assign = new Map<string, number>();
  const pack = (role: "voice" | "music", first: number) => {
    const ends: number[] = [];
    for (const c of project.audio.filter((x) => x.role === role).sort((a, b) => a.start - b.start)) {
      let k = ends.findIndex((e) => e <= c.start + 1e-6);
      if (k < 0) k = ends.push(0) - 1;
      ends[k] = c.start + clipDuration(project, c);
      assign.set(c.id, first + k);
    }
    return first + Math.max(1, ends.length);
  };
  pack("music", pack("voice", 0));
  edit((d) => {
    for (const c of d.audio) c.track = assign.get(c.id) ?? c.track;
  });
}

/** When a layer that isn't in view gets selected (say, from the canvas while scrubbing), jump to it. */
function useJumpToSelection(listed: Element[], times: Map<string, ElementTimes>) {
  const sel = useEditor((s) => (s.selection?.kind === "element" ? s.selection.id : null));
  useEffect(() => {
    if (!sel || listed.some((e) => e.id === sel)) return;
    const st = useEditor.getState();
    const tm = times.get(sel);
    if (!tm || st.playing || st.timeline.scope !== "scene") return;
    window.dispatchEvent(new CustomEvent("studio:seek", { detail: Math.min(tm.enter1, tm.end - 0.01) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);
}

// ------------------------------------------------------------------------------------- helpers

function laneX(e: { clientX: number }, lane: HTMLElement, pps: number): number {
  return (e.clientX - lane.getBoundingClientRect().left) / pps;
}

function scrubFrom(e: React.PointerEvent, pps: number, preview: Preview) {
  const lane = (e.currentTarget as HTMLElement).closest(".tl-inner")!.querySelector(".tl-ruler .tl-lane") as HTMLElement;
  if (useEditor.getState().playing) preview.pause();
  const set = (ev: { clientX: number }) => useEditor.getState().setTime(preview.frameTime(Math.max(0, laneX(ev, lane, pps))));
  set(e);
  useEditor.getState().select(null);
  const move = (ev: PointerEvent) => set(ev);
  const up = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
}

/** Snap a time to nearby targets (within SNAP_PX), else to the frame grid. */
function snapTime(t: number, targets: number[], pps: number, fps: number, off: boolean): number {
  if (!off) {
    let best = Infinity;
    let hit = t;
    for (const g of targets) {
      const d = Math.abs(g - t) * pps;
      if (d < best && d <= SNAP_PX) {
        best = d;
        hit = g;
      }
    }
    if (best !== Infinity) return hit;
  }
  return Math.round(t * fps) / fps;
}

function snapTargets(p: Project, skipId: string): number[] {
  const out = [useEditor.getState().time, 0];
  const times = elementTimes(p);
  for (const el of p.elements) {
    if (el.id === skipId) continue;
    const tm = times.get(el.id)!;
    out.push(tm.enter0, tm.enter1);
    if (Number.isFinite(tm.exit1)) out.push(tm.exit0, tm.exit1);
  }
  for (const c of p.audio) if (c.id !== skipId) out.push(c.start, c.start + clipDuration(p, c));
  for (const s of p.scenes) out.push(s.start);
  for (const m of p.markers) out.push(m.t);
  return out;
}

/** Generic horizontal drag producing a time delta, wrapped in one undo gesture. */
function dragTime(e: React.PointerEvent, pps: number, onDelta: (dt: number, ev: PointerEvent) => void) {
  e.stopPropagation();
  e.preventDefault();
  const x0 = e.clientX;
  const st = useEditor.getState();
  st.begin();
  const move = (ev: PointerEvent) => onDelta((ev.clientX - x0) / pps, ev);
  const up = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    useEditor.getState().end();
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
}

// ------------------------------------------------------------------------------------- rows

function Ruler({ pps, width, total, preview }: { pps: number; width: number; total: number; preview: Preview }) {
  const steps = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120];
  const step = steps.find((s) => s * pps >= 70) ?? 300;
  const ticks = [];
  for (let i = 0; i * step <= total; i++) ticks.push(Math.round(i * step * 100) / 100);
  return (
    <div className="tl-row tl-ruler">
      <div className="tl-label">
        <TimeReadout />
      </div>
      <div className="tl-lane" style={{ width }} onPointerDown={(e) => scrubFrom(e, pps, preview)}>
        {ticks.map((t) => (
          <div key={t} className="tick" style={{ left: t * pps }}>
            <span>{tickLabel(t, step)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function tickLabel(t: number, step: number): string {
  const digits = step < 0.5 ? 2 : step < 1 ? 1 : 0;
  if (t < 60) return `${t.toFixed(digits)}s`;
  const sec = (t % 60).toFixed(digits);
  return `${Math.floor(t / 60)}:${sec.padStart(digits ? digits + 3 : 2, "0")}`;
}

function TimeReadout() {
  const t = useEditor((s) => s.time);
  const fps = useEditor((s) => s.project?.output.fps ?? 30);
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  const f = Math.round((t - Math.floor(t)) * fps) % fps;
  return (
    <span className="timecode">
      {m}:{String(s).padStart(2, "0")}
      <small>:{String(f).padStart(2, "0")}</small>
    </span>
  );
}

function ScenesRow({ project, pps, total }: { project: Project; pps: number; total: number }) {
  const scenes = sortedScenes(project);
  const selected = useEditor((s) => (s.selection?.kind === "scene" ? s.selection.id : null));
  const fps = project.output.fps;
  const onEdge = (e: React.PointerEvent, i: number) => {
    const s0 = scenes[i];
    const lo = scenes[i - 1].start + 0.2;
    const hi = i + 1 < scenes.length ? scenes[i + 1].start - 0.2 : Infinity;
    const targets = snapTargets(project, s0.id).filter((t) => Math.abs(t - s0.start) > 1e-6);
    const base = project;
    const rippleLo = s0.start + rippleMin(base, s0.start);
    useEditor.getState().select({ kind: "scene", id: s0.id });
    dragTime(e, pps, (dt, ev) => {
      const st = useEditor.getState();
      if (st.timeline.ripple !== ev.shiftKey) {
        // the previous scene gets longer or shorter; everything after keeps its spacing
        const t = Math.max(rippleLo, snapTime(s0.start + dt, targets, pps, fps, ev.altKey));
        st.edit((d) => rippleShift(d as Project, base, s0.start, t - s0.start));
      } else {
        const t = Math.min(hi, Math.max(lo, snapTime(s0.start + dt, targets, pps, fps, ev.altKey)));
        st.edit((d) => {
          rippleShift(d as Project, base, 0, 0); // undo a ripple tried earlier in this drag
          d.scenes.find((x) => x.id === s0.id)!.start = +t.toFixed(4);
        });
      }
    });
  };
  return (
    <div className="tl-row tl-scenes">
      <div className="tl-label">
        <span>Scenes</span>
        <button className="icon tiny-icon" title="New scene at the playhead" onClick={addScene}><Plus size={13} /></button>
      </div>
      <div className="tl-lane">
        {scenes.map((s, i) => {
          const end = i + 1 < scenes.length ? scenes[i + 1].start : total;
          const tr = s.transition.type !== "cut" && i > 0 ? ` · ${s.transition.type}` : "";
          return (
            <div key={s.id} className={`scene-block ${selected === s.id ? "sel" : ""}`} style={{ left: s.start * pps, width: (end - s.start) * pps }} title={s.name}
              onPointerDown={(e) => {
                e.stopPropagation();
                useEditor.getState().select({ kind: "scene", id: s.id });
                const t = useEditor.getState().time;
                if (t < s.start || t >= end) window.dispatchEvent(new CustomEvent("studio:seek", { detail: s.start })); // into the scene
              }}>
              {i > 0 && <div className="scene-edge" onPointerDown={(e) => onEdge(e, i)} title="Drag to move the scene start" />}
              {s.number ? `${s.number} · ` : ""}
              {s.name}
              {s.startMode === "build" && i > 0 && <em> build</em>}
              {tr && <em className="tr">{tr}</em>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CameraRow({ project, pps, preview, toggle }: { project: Project; pps: number; preview: Preview; toggle?: boolean }) {
  useSyncExternalStore((cb) => preview.onChange(cb), () => preview.version);
  const selected = useEditor((s) => (s.selection?.kind === "camkey" ? s.selection.id : null));
  const { auto } = preview.cameraKeys();
  const manual = project.camera.keyframes;
  const overridden = new Set(manual.map((k) => k.id));
  const fps = project.output.fps;
  const onKey = (e: React.PointerEvent, id: string, t0: number) => {
    useEditor.getState().select({ kind: "camkey", id });
    dragTime(e, pps, (dt, ev) => {
      const t = Math.max(0, snapTime(t0 + dt, snapTargets(project, id), pps, fps, ev.altKey));
      useEditor.getState().edit((d) => {
        const k = d.camera.keyframes.find((x) => x.id === id);
        if (k) k.t = +t.toFixed(4);
      });
    });
  };
  return (
    <div className="tl-row tl-camera">
      <div className="tl-label" title={project.camera.auto ? "Auto-camera on (hollow keys); drag a key to override it" : "Camera keyframes"}>
        {toggle && (
          <button className="icon tiny-icon fold" title="Fold camera, markers, captions and comments into one overview row"
            onClick={() => useEditor.getState().setTimeline({ expanded: false })}><ChevronDown size={13} /></button>
        )}
        <Camera size={13} />
        <span>Camera{project.camera.auto ? " (auto)" : ""}</span>
      </div>
      <div className="tl-lane">
        {project.camera.auto &&
          auto.filter((k) => !overridden.has(k.id)).map((k) => (
            <div key={k.id} className={`cam-key auto ${selected === k.id ? "sel" : ""}`} style={{ left: k.t * pps - 5 }} title={`auto ${k.t.toFixed(2)} s · select, then Override in the inspector`}
              onPointerDown={(e) => { e.stopPropagation(); useEditor.getState().select({ kind: "camkey", id: k.id }); }} />
          ))}
        {manual.map((k) => (
          <div key={k.id} className={`cam-key ${selected === k.id ? "sel" : ""}`} style={{ left: k.t * pps - 5 }} title={`${k.t.toFixed(2)} s · zoom ${k.zoom}`}
            onPointerDown={(e) => onKey(e, k.id, k.t)} />
        ))}
      </div>
    </div>
  );
}

function MarkersRow({ project, pps }: { project: Project; pps: number }) {
  const selected = useEditor((s) => (s.selection?.kind === "marker" ? s.selection.id : null));
  const fps = project.output.fps;
  return (
    <div className="tl-row tl-markers">
      <div className="tl-label" title="Press M (also while playing) to drop a marker at the playhead">
        <Flag size={13} />
        <span>Markers (M)</span>
      </div>
      <div className="tl-lane">
        {project.markers.map((m) => (
          <div key={m.id} className={`marker ${selected === m.id ? "sel" : ""}`} style={{ left: m.t * pps }} title={`${m.label} · ${m.t.toFixed(2)} s`}
            onPointerDown={(e) => {
              useEditor.getState().select({ kind: "marker", id: m.id });
              const t0 = m.t;
              dragTime(e, pps, (dt, ev) => {
                const t = Math.max(0, snapTime(t0 + dt, snapTargets(project, m.id), pps, fps, ev.altKey));
                useEditor.getState().edit((d) => void (d.markers.find((x) => x.id === m.id)!.t = +t.toFixed(4)));
              });
            }}>
            <span>{m.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const ICON = { image: ImageIcon, text: Type, shape: Shapes };

function useSelected(id: string) {
  return useEditor((s) => (s.selection?.kind === "element" && s.selection.id === id) || s.multi.includes(id));
}

const ElementRow = memo(function ElementRow({ el, project, pps, total, other }: { el: Element; project: Project; pps: number; total: number; other?: boolean }) {
  const selected = useSelected(el.id);
  const Icon = ICON[el.type];
  return (
    <div className={`tl-row ${selected ? "selected" : ""} ${other ? "other" : ""}`}>
      <div className="tl-label" onPointerDown={(e) => selectElement(el.id, e.shiftKey || e.ctrlKey || e.metaKey)}
        title={other ? `${el.name} (still on the board from an earlier scene)` : el.name}>
        <Icon size={13} />
        <span>{el.name || el.type}</span>
      </div>
      <div className="tl-lane">
        <ElementBars el={el} project={project} pps={pps} total={total} />
      </div>
    </div>
  );
});

/** Several layers sharing one row (Pack layers): names are written next to their bars. */
const LaneRow = memo(function LaneRow({ index, lane, project, pps, total }: { index: number; lane: Element[]; project: Project; pps: number; total: number }) {
  const selected = useEditor((s) => lane.some((el) => (s.selection?.kind === "element" && s.selection.id === el.id) || s.multi.includes(el.id)));
  return (
    <div className={`tl-row ${selected ? "selected" : ""}`}>
      <div className="tl-label" title={lane.map((el) => el.name).join("\n")}>
        <Layers size={13} />
        <span>Layer {index + 1}</span>
      </div>
      <div className="tl-lane">
        {lane.map((el) => <ElementBars key={el.id} el={el} project={project} pps={pps} total={total} named />)}
      </div>
    </div>
  );
});

/** One layer's bars: entrance (outline · fill), hold, exit, with handles to retime them. */
const ElementBars = memo(function ElementBars({ el, project, pps, total, named }: { el: Element; project: Project; pps: number; total: number; named?: boolean }) {
  const selected = useSelected(el.id);
  const tm = elementTimes(project).get(el.id)!;
  const fps = project.output.fps;
  const select = (e?: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) =>
    selectElement(el.id, !!e && (e.shiftKey || e.ctrlKey || e.metaKey));
  const holdEnd = Math.min(Number.isFinite(tm.exit0) ? tm.exit0 : INF, tm.cleared, total);
  const exitEnd = Math.min(tm.exit1, tm.cleared);
  const draw = el.enter.mode === "draw" || el.enter.mode === "outline";

  const edit = (fn: (d: Element) => void) =>
    useEditor.getState().edit((p) => {
      fn(p.elements.find((x) => x.id === el.id)!);
    });

  const onMove = (e: React.PointerEvent) => {
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      e.stopPropagation();
      return select(e);
    }
    const group = selectedElements();
    if (!(group.includes(el.id) && group.length > 1)) select();
    if (el.locked) return;
    const start0 = el.start;
    const others = new Map(project.elements.filter((x) => x.id !== el.id && selectedElements().includes(x.id) && !x.locked).map((x) => [x.id, x.start]));
    const targets = snapTargets(project, el.id);
    const len = tm.enter1 - tm.enter0;
    dragTime(e, pps, (dt, ev) => {
      // snap either the start or the end of the entrance
      let s = tm.enter0 + dt;
      const sStart = snapTime(s, targets, pps, fps, ev.altKey);
      const sEnd = snapTime(s + len, targets, pps, fps, ev.altKey) - len;
      s = Math.abs(sStart - s) <= Math.abs(sEnd - s) ? sStart : sEnd;
      const delta = s - tm.enter0;
      useEditor.getState().edit((p) => {
        const me = p.elements.find((x) => x.id === el.id)!;
        me.start = Math.max(0, +(start0 + delta).toFixed(4));
        for (const [id, st0] of others) {
          const o = p.elements.find((x) => x.id === id);
          if (o) o.start = Math.max(0, +(st0 + delta).toFixed(4));
        }
      });
    });
  };
  const onEdge = (e: React.PointerEvent, which: "outline" | "enter" | "hold" | "exit") => {
    select();
    const targets = snapTargets(project, el.id);
    dragTime(e, pps, (dt, ev) => {
      const snapAbs = (t: number) => snapTime(t, targets, pps, fps, ev.altKey);
      edit((d) => {
        if (which === "outline") d.enter.outlineDur = Math.max(0.05, +(snapAbs(tm.outline1 + dt) - tm.enter0).toFixed(3));
        else if (which === "enter") {
          const v = Math.max(0.05, +(snapAbs(tm.enter1 + dt) - tm.outline1).toFixed(3));
          if (el.enter.mode === "draw" && (el.type === "image" || el.shape?.fill)) d.enter.fillDur = v;
          else if (el.enter.mode === "draw" || el.enter.mode === "outline") d.enter.outlineDur = v;
          else d.enter.duration = v;
        } else if (which === "hold") d.hold = Math.max(0, +(snapAbs(tm.exit0 + dt) - tm.enter1).toFixed(3));
        else d.exit.duration = Math.max(0.05, +(snapAbs(tm.exit1 + dt) - tm.exit0).toFixed(3));
      });
    });
  };

  return (
    <>
        <div className={`bar ${selected ? "sel" : ""}`} style={{ left: tm.enter0 * pps }} onPointerDown={onMove} title={el.name}>
          {draw && tm.outline1 > tm.enter0 && <div className="seg outline" style={{ width: (tm.outline1 - tm.enter0) * pps }} />}
          <div className={`seg ${draw ? "fill" : el.enter.mode}`} style={{ width: (tm.enter1 - tm.outline1) * pps }} />
        </div>
        {draw && tm.outline1 > tm.enter0 && tm.enter1 > tm.outline1 && (
          <div className="edge" style={{ left: tm.outline1 * pps - 3 }} onPointerDown={(e) => onEdge(e, "outline")} title="Outline duration" />
        )}
        {el.enter.mode !== "instant" && <div className="edge" style={{ left: tm.enter1 * pps - 3 }} onPointerDown={(e) => onEdge(e, "enter")} title="Entrance duration" />}
        {holdEnd > tm.enter1 && <div className={`seg hold ${Number.isFinite(tm.exit0) ? "" : "open"}`} style={{ left: tm.enter1 * pps, width: (holdEnd - tm.enter1) * pps }} onPointerDown={select} />}
        {Number.isFinite(tm.exit0) && tm.exit0 < tm.cleared && (
          <>
            <div className="seg exit" style={{ left: tm.exit0 * pps, width: Math.max(2, (exitEnd - tm.exit0) * pps) }} onPointerDown={(e) => onEdge(e, "hold")} title="Drag to change the hold" />
            <div className="edge" style={{ left: exitEnd * pps - 3 }} onPointerDown={(e) => onEdge(e, "exit")} title="Exit duration" />
          </>
        )}
        {named && <span className={`bar-name ${selected ? "sel" : ""}`} style={{ left: tm.enter1 * pps + 4 }} onPointerDown={onMove}>{el.name || el.type}</span>}
    </>
  );
});

const peakCache = new Map<string, Promise<{ perSecond: number; peaks: number[] }>>();
function usePeaks(pid: string, assetId: string) {
  const [data, setData] = useState<{ perSecond: number; peaks: number[] } | null>(null);
  useEffect(() => {
    let p = peakCache.get(assetId);
    if (!p) {
      p = fetch(fileUrl(pid, `derived/${assetId}/peaks.json`)).then((r) => r.json());
      peakCache.set(assetId, p);
    }
    let live = true;
    p.then((d) => live && setData(d)).catch(() => peakCache.delete(assetId));
    return () => {
      live = false;
    };
  }, [pid, assetId]);
  return data;
}

function AudioRow({ track, label, project, pps, first }: { track: number; label: string; project: Project; pps: number; first: boolean }) {
  const clips = project.audio.filter((c) => c.track === track);
  const Icon = label.startsWith("Music") ? Music : Mic;
  return (
    <div className="tl-row tl-audio">
      <div className="tl-label">
        <Icon size={13} />
        <span>{label}</span>
        {first && project.audio.length > 1 && (
          <button className="icon tiny-icon" title="Tidy tracks: voice clips share as few tracks as they can without overlapping, music below (undo restores)"
            onClick={tidyTracks}><Wand2 size={13} /></button>
        )}
      </div>
      <div className="tl-lane">
        {clips.map((c) => (
          <Clip key={c.id} clip={c} project={project} pps={pps} />
        ))}
      </div>
    </div>
  );
}

function Clip({ clip, project, pps }: { clip: AudioClip; project: Project; pps: number }) {
  const selected = useEditor((s) => s.selection?.kind === "clip" && s.selection.id === clip.id);
  const asset = project.assets.find((a) => a.id === clip.assetId);
  const peaks = usePeaks(project.id, clip.assetId);
  const len = clipDuration(project, clip);
  const canvas = useRef<HTMLCanvasElement>(null);
  const w = Math.max(2, len * pps);
  const fps = project.output.fps;

  useEffect(() => {
    const cv = canvas.current;
    if (!cv || !peaks) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = Math.min(16000, Math.round(w * dpr));
    cv.height = Math.round(34 * dpr);
    const ctx = cv.getContext("2d")!;
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.fillStyle = clip.role === "music" ? "#b6a3e8" : "#7fc8b0";
    const mid = cv.height / 2;
    for (let x = 0; x < cv.width; x++) {
      const t = clip.trimIn + (x / cv.width) * len;
      const i0 = Math.floor(t * peaks.perSecond);
      const i1 = Math.max(i0 + 1, Math.floor((clip.trimIn + ((x + 1) / cv.width) * len) * peaks.perSecond));
      let m = 0;
      for (let i = i0; i < i1 && i < peaks.peaks.length; i++) m = Math.max(m, peaks.peaks[i]);
      const h = Math.min(1, m * 1.6 * clip.volume) * mid;
      ctx.fillRect(x, mid - h, 1, Math.max(1, 2 * h));
    }
  }, [peaks, w, clip.trimIn, len, clip.volume, clip.role]);

  const edit = (fn: (c: AudioClip) => void) =>
    useEditor.getState().edit((p) => {
      fn(p.audio.find((x) => x.id === clip.id)!);
    });
  const select = () => useEditor.getState().select({ kind: "clip", id: clip.id });
  const srcLen = asset?.duration ?? len;

  const onMove = (e: React.PointerEvent) => {
    select();
    const s0 = clip.start;
    const targets = snapTargets(project, clip.id);
    const base = project;
    const rippleLo = s0 + rippleMin(base, s0, clip.role);
    dragTime(e, pps, (dt, ev) => {
      let s = s0 + dt;
      const a = snapTime(s, targets, pps, fps, ev.altKey);
      const b = snapTime(s + len, targets, pps, fps, ev.altKey) - len;
      s = Math.abs(a - s) <= Math.abs(b - s) ? a : b;
      const st = useEditor.getState();
      if (st.timeline.ripple !== ev.shiftKey) {
        // later clips of this role, the scenes and their layers follow
        s = Math.max(rippleLo, s);
        st.edit((d) => rippleShift(d as Project, base, s0, s - s0, clip.role));
      } else
        st.edit((d) => {
          rippleShift(d as Project, base, 0, 0, clip.role); // undo a ripple tried earlier in this drag
          d.audio.find((x) => x.id === clip.id)!.start = Math.max(0, +s.toFixed(4));
        });
    });
  };
  const onTrim = (e: React.PointerEvent, side: "in" | "out") => {
    select();
    const c0 = { ...clip };
    const out0 = clip.trimOut ?? srcLen;
    dragTime(e, pps, (dt) => {
      edit((c) => {
        if (side === "in") {
          const d = Math.min(Math.max(-c0.trimIn, dt), out0 - c0.trimIn - 0.1);
          c.trimIn = +(c0.trimIn + d).toFixed(4);
          c.start = Math.max(0, +(c0.start + d).toFixed(4));
        } else c.trimOut = +Math.min(srcLen, Math.max(c0.trimIn + 0.1, out0 + dt)).toFixed(4);
      });
    });
  };

  return (
    <div className={`clip ${clip.role} ${selected ? "selected" : ""}`} style={{ left: clip.start * pps, width: w }} onPointerDown={onMove} title={asset?.name}>
      <canvas ref={canvas} style={{ width: w, height: 34 }} />
      <span className="clip-name">{asset?.name ?? clip.assetId}</span>
      <div className="trim in" onPointerDown={(e) => onTrim(e, "in")} />
      <div className="trim out" onPointerDown={(e) => onTrim(e, "out")} />
    </div>
  );
}

function Playhead({ pps, scroller }: { pps: number; scroller: React.RefObject<HTMLDivElement | null> }) {
  const t = useEditor((s) => s.time);
  const playing = useEditor((s) => s.playing);
  const x = LABEL_W + t * pps;
  // keep the playhead in view while playing and after jumps (seek, Home/End, undo)
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const out = x > el.scrollLeft + el.clientWidth - 40 || x < el.scrollLeft + LABEL_W;
    if (out) el.scrollLeft = playing ? x - LABEL_W - 40 : x - LABEL_W - el.clientWidth / 3;
  }, [x, playing, scroller]);
  return <div className="playhead" style={{ left: x }} />;
}

/** Camera keys, markers, captions and comments in one row; expand for a row each (and dragging). */
function OverviewRow({ project, pps, preview }: { project: Project; pps: number; preview: Preview }) {
  useSyncExternalStore((cb) => preview.onChange(cb), () => preview.version);
  const comments = useEditor((s) => s.comments);
  const keys = project.camera.auto ? preview.cameraKeys().auto : project.camera.keyframes;
  const seek = (t: number) => (e: React.PointerEvent) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent("studio:seek", { detail: t }));
  };
  return (
    <div className="tl-row tl-overview">
      <div className="tl-label" title="Camera keys, markers, captions and comments. Expand to edit them on their own rows.">
        <button className="icon tiny-icon fold" title="Show camera, markers, captions and comments as separate rows"
          onClick={() => useEditor.getState().setTimeline({ expanded: true })}><ChevronRight size={13} /></button>
        <span>Overview</span>
      </div>
      <div className="tl-lane">
        {project.subtitles.map((c) => (
          <div key={c.id} className="ov-cue" style={{ left: c.t0 * pps, width: Math.max(2, (c.t1 - c.t0) * pps) }} title={`Caption: ${c.text}`} />
        ))}
        {keys.map((k) => (
          <div key={k.id} className={`ov-cam ${project.camera.auto ? "auto" : ""}`} style={{ left: k.t * pps - 4 }} title={`Camera ${k.t.toFixed(2)} s`} onPointerDown={seek(k.t)} />
        ))}
        {project.markers.map((m) => (
          <div key={m.id} className="ov-marker" style={{ left: m.t * pps }} title={`Marker ${m.label} · ${m.t.toFixed(2)} s`} onPointerDown={seek(m.t)} />
        ))}
        {comments.map((c) => (
          <div key={c.id} className={`ov-comment ${c.resolved ? "resolved" : ""}`} style={{ left: c.t * pps - 4 }} title={`${c.name}: ${c.text}`} onPointerDown={seek(c.t)} />
        ))}
      </div>
    </div>
  );
}

/** Above the layer rows: what is shown, and the layout switches. */
interface Zoom {
  pps: number;
  /** seconds visible across the lanes */
  span: number;
  set: (pps: number) => void;
  fitAll: () => void;
  fitScene: (() => void) | null;
}

// the slider is logarithmic: equal steps look like equal zoom changes
const toSlider = (pps: number) => Math.round((Math.log(pps / MIN_PPS) / Math.log(MAX_PPS / MIN_PPS)) * 1000);
const fromSlider = (v: number) => MIN_PPS * Math.pow(MAX_PPS / MIN_PPS, v / 1000);
const spanLabel = (s: number) => (s >= 90 ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}` : s >= 10 ? `${Math.round(s)} s` : `${s.toFixed(1)} s`);

function ZoomControls({ zoom }: { zoom: Zoom }) {
  return (
    <span className="tl-zoom">
      <button className="tiny-icon icon" onClick={() => zoom.set(zoom.pps / 1.5)} title="Zoom out (-)"><ZoomOut size={13} /></button>
      <input type="range" min={0} max={1000} value={toSlider(zoom.pps)} onChange={(e) => zoom.set(fromSlider(Number(e.target.value)))}
        title="Zoom (Ctrl+wheel or pinch over the timeline)" />
      <button className="tiny-icon icon" onClick={() => zoom.set(zoom.pps * 1.5)} title="Zoom in (=)"><ZoomIn size={13} /></button>
      <span className="muted tl-span" title="Time visible across the timeline">{spanLabel(zoom.span)}</span>
      <button className="tiny" onClick={zoom.fitAll} title={"Show the whole video (\\)"}><Maximize2 size={11} /> Fit</button>
      {zoom.fitScene && <button className="tiny" onClick={zoom.fitScene} title="Zoom to the scene under the playhead">Scene</button>}
    </span>
  );
}

function LayersHeader({ view, count, scene, zoom }: { view: TimelineView; count: number; scene: { name: string; number?: string | null } | null; zoom: Zoom }) {
  const set = useEditor((s) => s.setTimeline);
  return (
    <div className="tl-row tl-layers-head">
      <div className="tl-label">
        <Layers size={13} />
        <span>Layers ({count})</span>
      </div>
      <div className="tl-lane">
        <div className="tl-tools">
          <span className="segmented small-seg">
            <button className={view.scope === "scene" ? "on" : ""} onClick={() => set({ scope: "scene" })} title="Only the layers of the scene under the playhead">This scene</button>
            <button className={view.scope === "all" ? "on" : ""} onClick={() => set({ scope: "all" })} title="Every layer in the project">All scenes</button>
          </span>
          <label title="Layers that don't overlap in time share a row"><input type="checkbox" checked={view.pack} onChange={(e) => set({ pack: e.target.checked })} /> Pack layers</label>
          <label title="Shorter rows"><input type="checkbox" checked={view.compact} onChange={(e) => set({ compact: e.target.checked })} /> Compact</label>
          <label className={view.ripple ? "ripple-on" : ""}
            title={"Moving a scene's start or an audio clip also moves everything after it (later scenes and their layers, voice clips, markers, captions), so a slide gets longer or shorter without breaking the rest.\nHold Shift while dragging to do the opposite of this setting."}>
            <input type="checkbox" checked={view.ripple} onChange={(e) => set({ ripple: e.target.checked })} /> Ripple
          </label>
          <ZoomControls zoom={zoom} />
          {scene && <span className="muted">Scene {scene.number ? `${scene.number} · ` : ""}{scene.name}</span>}
        </div>
      </div>
    </div>
  );
}

function CaptionsRow({ project, pps }: { project: Project; pps: number }) {
  return (
    <div className="tl-row tl-captions">
      <div className="tl-label" title="Timed caption cues (click one to edit)">
        <Captions size={13} />
        <span>Captions</span>
      </div>
      <div className="tl-lane">
        {project.subtitles.map((c) => (
          <div key={c.id} className="cue" style={{ left: c.t0 * pps, width: Math.max(2, (c.t1 - c.t0) * pps) }} title={c.text}
            onPointerDown={(e) => { e.stopPropagation(); useEditor.getState().set({ panel: "subtitles" }); }}>
            {c.text.replace("\n", " ")}
          </div>
        ))}
      </div>
    </div>
  );
}

function CommentsRow({ pps }: { pps: number }) {
  const comments = useEditor((s) => s.comments);
  if (comments.length === 0) return null;
  return (
    <div className="tl-row tl-comments">
      <div className="tl-label" title="Reviewer comments (Review button)">
        <MessageSquare size={13} />
        <span>Comments</span>
      </div>
      <div className="tl-lane">
        {comments.map((c) => (
          <div key={c.id} className={`comment-pin ${c.resolved ? "resolved" : ""}`} style={{ left: c.t * pps - 6 }} title={`${c.name}: ${c.text}`}
            onPointerDown={(e) => {
              e.stopPropagation();
              window.dispatchEvent(new CustomEvent("studio:seek", { detail: c.t }));
              useEditor.getState().set({ panel: "share" });
            }}>
            <MessageSquare size={12} />
          </div>
        ))}
      </div>
    </div>
  );
}
